"use client";

import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { CameraControls, ContactShadows, Environment } from '@react-three/drei';

import { Avatar } from './Avatar';
import { AvatarProvider, useAvatar } from '../contexts/AvatarContext';
import { useVoiceQueue } from '../hooks/useVoiceQueue';
import { AvatarPlayerProps, AvatarPlayerRef, VoiceSegment, QueueSegment, CameraPosition } from '../types/avatar';
import { defaultSkinnedMesh } from './DefaultSkinnedMesh';
import { Leva } from 'leva';


// Define default camera positions as constants for stable references
const DEFAULT_CAMERA_ZOOM_IN: CameraPosition = [0, 1.5, 1.5, 0, 1.5, 0, true];
const DEFAULT_CAMERA_ZOOM_OUT: CameraPosition = [0, 2.2, 5, 0, 1.0, 0, true];
const DEFAULT_CAMERA_DEFAULT: CameraPosition = [0, 1.5, 1.5, 0, 1.5, 0, true];

// Internal component that uses the avatar context
const AvatarPlayerInternal = forwardRef<AvatarPlayerRef, AvatarPlayerProps>(({
  skinnedMesh = defaultSkinnedMesh,
  modelPath = "/models/64f1a714fe61576b46f27ca2.glb",
  animationPath = "/models/animations_female.glb",
  voiceQueue: requestedVoiceQueue = [],
  messageQueue = [],
  emotion = 'default',
  animation = 'Idle',
  onComplete: userOnComplete,
  onSegmentComplete,
  cameraZoomIn = DEFAULT_CAMERA_ZOOM_IN,
  cameraZoomOut = DEFAULT_CAMERA_ZOOM_OUT,
  cameraDefault = DEFAULT_CAMERA_DEFAULT,
  visemeOverride,
}, ref) => {
  const { setAnimation, setEmotion } = useAvatar();

  const [queue, setQueue] = useState<QueueSegment[]>([]);
  const [pendingQueue, setPendingQueue] = useState<QueueSegment[] | null>(null);
  const cameraControlsRef = useRef<CameraControls>(null);

  // Combine queues if needed, or prefer one. 
  // Logic: If messageQueue is provided and non-empty, use it. Otherwise use voiceQueue.
  // Ideally we might want to merge them, but for now let's treat them as sources.
  // Actually, let's just merge them into the initial state if provided.

  const {
    currentSegment,
    currentIndex,
    isPlaying,
    currentViseme,
    nextSegment,
    previousSegment,
    playSegment,
    stopPlayback,
    resetQueue,
  } = useVoiceQueue(queue, handleOnComplete, onSegmentComplete);

  const isIdleRef = useRef(true);

  // Update idle ref whenever relevant state changes
  useEffect(() => {
    isIdleRef.current = currentIndex >= queue.length && !isPlaying;
  }, [currentIndex, queue.length, isPlaying]);

  // Camera control functions
  const setCameraPosition = (position: CameraPosition) => {
    if (cameraControlsRef.current) {
      const [x, y, z, targetX, targetY, targetZ, enableTransition] = position;
      // console.log('Setting camera position:', { x, y, z, targetX, targetY, targetZ, enableTransition });
      cameraControlsRef.current.setLookAt(x, y, z, targetX, targetY, targetZ, enableTransition);
    } else {
      console.warn('CameraControls ref not available');
    }
  };

  // Expose methods imperatively
  useImperativeHandle(ref, () => ({
    loadQueue: (newQueue: QueueSegment[]) => {
      if (isIdleRef.current) {
        setQueue(newQueue);
        resetQueue(); // Reset to start playing the new queue
      } else {
        setPendingQueue(newQueue);
      }
    },
    setCameraZoomIn: () => setCameraPosition(cameraZoomIn),
    setCameraZoomOut: () => setCameraPosition(cameraZoomOut),
    setCameraDefault: () => setCameraPosition(cameraDefault),
  }));

  // Combine queues: VoiceQueue first, then MessageQueue (or however the user prefers, but concatenation is safest for "both")
  // We use a ref to track if we've already loaded the initial queues to avoid infinite loops if they change
  const [combinedQueue, setCombinedQueue] = useState<QueueSegment[]>([]);

  useEffect(() => {
    // Merge queues when props change
    const newQueue = [...requestedVoiceQueue, ...messageQueue];
    if (newQueue.length > 0) {
      if (isIdleRef.current) {
        setQueue(newQueue);
        resetQueue();
      } else {
        setPendingQueue(newQueue);
      }
    }
  }, [requestedVoiceQueue, messageQueue, resetQueue]);

  function handleOnComplete() {
    userOnComplete?.();
    if (pendingQueue) {
      setQueue(pendingQueue);
      setPendingQueue(null);
      resetQueue(); // Reset to start playing the pending queue
    }
  }

  // Update avatar state when current segment changes
  useEffect(() => {
    if (currentSegment) {
      setEmotion(currentSegment.emotion);
      if (currentSegment.animation) {
        setAnimation(currentSegment.animation);
      }
    } else {
      // Set defaults when no segment is playing
      setEmotion(emotion);
      setAnimation(animation);
    }
  }, [currentSegment, setAnimation, setEmotion, emotion, animation]);



  return (
    <>
      <Leva hidden />
      <Canvas
        camera={{ position: [0, 0, 5], fov: 30 }}
        style={{ height: '100vh', width: '100vw' }}
      >
        <CameraControls ref={cameraControlsRef} />

        <ambientLight intensity={0.3} />

        <directionalLight
          position={[5, 10, 5]}
          intensity={1.2}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />

        <directionalLight
          position={[-5, 5, -5]}
          intensity={0.5}
        />

        <spotLight
          position={[0, 5, -5]}
          angle={0.3}
          intensity={0.8}
          penumbra={0.5}
          castShadow
        />

        <Environment preset="sunset" />

        <ContactShadows opacity={0.7} />

        <Avatar
          skinnedMesh={skinnedMesh}
          modelPath={modelPath}
          animationPath={animationPath}
          visemeOverride={visemeOverride || currentViseme || undefined}
          onLoaded={() => {
            // Set initial camera position when avatar is loaded
            if (cameraControlsRef.current) {
              setCameraPosition(cameraDefault);
            }
          }}
        />
      </Canvas>
    </>

  );
});

// Main AvatarPlayer component with context provider
export const AvatarPlayer = forwardRef<AvatarPlayerRef, AvatarPlayerProps>((props, ref) => {
  return (
    <AvatarProvider>
      <AvatarPlayerInternal {...props} ref={ref} />
    </AvatarProvider>
  );
});